import { useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";

import {
  TeamMemberTaskStatus,
  type TeamMemberTask,
  type UpdateTeamMemberTaskDto,
} from "@fitness-app/data-models/entities/TeamMemberTask";
import { type Database, type Json } from "@fitness-app/supabase";
import { getErrorMessage } from "@fitness-app/utils";

import { analyticsService, supabase } from "~/store/initializeStore";
import { TEAM_MEMBER_TASKS_QUERY_KEY } from "./useTeamMemberTasks";
import { TEAM_MEMBER_TASKS_COUNT_QUERY_KEY } from "./useTeamMemberTasksCount";
import { TEAM_MEMBER_TASKS_DUE_TODAY_PLANNED_COUNT_QUERY_KEY } from "./useTeamMemberTasksDueTodayOrPlannedCount";

interface UpdateParams {
  id: string;
  data: UpdateTeamMemberTaskDto;
}

type TeamMemberTaskUpdate = Database["public"]["Tables"]["team_member_task"]["Update"];

export const useUpdateTeamMemberTask = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }: UpdateParams) => {
      const { metadata, reminders, recurrence, startDate, endDate, dueDate, ...rest } = data;

      const updateData: TeamMemberTaskUpdate = {
        ...rest,
        updatedAt: new Date().toISOString(),
      };

      // Add JSON fields only if they are explicitly provided
      if (metadata !== undefined) {
        updateData.metadata = metadata as Json;
      }
      if (reminders !== undefined) {
        updateData.reminders = reminders as Json;
      }
      if (recurrence !== undefined) {
        updateData.recurrence = recurrence as Json;
      }

      // Add TaskDateTime fields as JSONB
      if (startDate !== undefined) {
        updateData.startDate = startDate as unknown as Json;
      }
      if (endDate !== undefined) {
        updateData.endDate = endDate as unknown as Json;
      }
      if (dueDate !== undefined) {
        updateData.dueDate = dueDate as unknown as Json;
      }

      // If completing task, set completedAt
      if (data.status === TeamMemberTaskStatus.Completed) {
        updateData.completedAt = new Date().toISOString();
      }

      // If uncompleting task, clear completedAt
      if (data.status && data.status !== TeamMemberTaskStatus.Completed) {
        updateData.completedAt = null;
      }

      const { data: updated } = await supabase
        .from("team_member_task")
        .update(updateData)
        .eq("id", id)
        .select()
        .single()
        .throwOnError();

      return updated;
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: [TEAM_MEMBER_TASKS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [TEAM_MEMBER_TASKS_COUNT_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [TEAM_MEMBER_TASKS_DUE_TODAY_PLANNED_COUNT_QUERY_KEY] });

      const updateType =
        variables.data.status === TeamMemberTaskStatus.Completed
          ? "completed"
          : variables.data.status
            ? "status_changed"
            : variables.data.dueDate !== undefined
              ? "date_changed"
              : "updated";

      void analyticsService.track("team_member_task_updated", {
        updateType,
        newStatus: variables.data.status,
      });
    },
    onError: (error) => {
      void message.error(`Nie udało się zaktualizować zadania: ${getErrorMessage(error)}`);
    },
  });

  const toggleComplete = async (task: TeamMemberTask) => {
    const newStatus =
      task.status === TeamMemberTaskStatus.Completed ? TeamMemberTaskStatus.Pending : TeamMemberTaskStatus.Completed;

    await mutation.mutateAsync({
      id: task.id,
      data: { status: newStatus },
    });

    if (newStatus === TeamMemberTaskStatus.Completed) {
      void message.success("Zadanie zostało ukończone");
    }
  };

  return {
    mutation,
    toggleComplete,
    isLoading: mutation.isLoading,
  };
};
